import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Download, Copy } from 'lucide-react';
import Button from '../Button';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface StackedData {
  label: string;
  value: number;
  color: string;
}

interface VisualizationData {
  category?: string;
  name?: string;
  value: string;
  percentage?: string;
  breakdown?: string;
  stacked_data?: StackedData[];
}

interface Visualization {
  type: 'bar_chart' | 'pie_chart' | 'table';
  title: string;
  data: VisualizationData[];
}

interface StackedBarChartProps {
  visualization: Visualization;
  questionType?: string;
  className?: string;
}

// Custom tooltip
const CustomTooltip = ({ active, payload, label }: { active?: boolean, payload?: any[], label?: string }) => {
  if (active && payload && payload.length > 0) {
    return (
      <div className="bg-white p-2 border rounded shadow text-xs">
        <div className="font-semibold mb-1">{label}</div>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span>{entry.name}: {entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const StackedBarChart: React.FC<StackedBarChartProps> = ({ 
  visualization, 
  className = '' 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  
  if (!visualization?.data || !visualization.data[0]?.stacked_data) return null;

  // Prepare data for Recharts - deeper, more visible colors based on BluePill brand
  const themeColors = {
    '1st Place': '#07E5D1',  // Primary Teal (bright and visible)
    '2nd Place': '#06D4C2',  // Primary Hover (darker teal)
    '3rd Place': '#059B8E'   // Much darker teal for visibility
  };

  // Transform data for Recharts
  const chartData = visualization.data.map(item => {
    const dataPoint: any = {
      name: item.category || item.name || ''
    };
    
    item.stacked_data?.forEach((segment) => {
      dataPoint[segment.label] = segment.value;
    });
    
    return dataPoint;
  });

  // Get unique stack labels for bars
  const stackLabels = visualization.data[0]?.stacked_data?.map(s => s.label) || [];

  const handleCopy = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff' });
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      });
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!chartRef.current) return;
    const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = 'chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className={`bg-white p-4 rounded-lg border border-gray-200 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-800">{visualization.title}</h4>
        <div className="flex gap-2 no-print">
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDownload}
              title="Download chart as image"
              onMouseEnter={() => setShowTooltip('download')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'download' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Download chart as image
              </div>
            )}
          </div>
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              icon={<Copy className="h-4 w-4" />}
              onClick={handleCopy}
              title="Copy chart as image"
              onMouseEnter={() => setShowTooltip('copy')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {''}
            </Button>
            {showTooltip === 'copy' && (
              <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                Copy chart as image
              </div>
            )}
          </div>
          {copied && (
            <span className="text-green-600 text-xs ml-1">Copied!</span>
          )}
        </div>
      </div>

      <div ref={chartRef} className="w-full" style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              fontSize={12}
              interval={0}
              tick={{ fill: '#666' }}
            />
            <YAxis 
              fontSize={12}
              stroke="#000"
              strokeWidth={2}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              content={() => null}
            />
            {stackLabels.map((label) => (
              <Bar 
                key={label}
                dataKey={label} 
                stackId="a"
                fill={themeColors[label as keyof typeof themeColors] || '#E5E7EB'}
                barSize={40}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Custom Legend with proper color dots */}
      <div className="flex justify-center items-center space-x-6 mt-2 pt-3 border-t border-gray-200">
        {stackLabels.map((label) => (
          <div key={label} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: themeColors[label as keyof typeof themeColors] || '#E5E7EB' }}
            />
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StackedBarChart;